import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify';
import Page from '../../components/layout/page.component'
import { FileResponse } from '../../components/interfaces/FileResponse';
import Loader from '../../components/loader/loader.component';
import SheetItem from '../../components/search/sheet-item.component';
import styles from '../../index.module.css'
import { useRepertoireService } from '../../services/repertoire.service';
import "react-slideshow-image/dist/styles.css";
import { Slide, SlideshowRef } from 'react-slideshow-image';
import { arrayMove } from '@dnd-kit/sortable';
import FileHelper from '../../helpers/filehelper';
import { StringHelper } from '../../components/infra/stringHelper.component';
import { DownloadIcon, EraserIcon, GalleryIcon, ListIcon, MaximizeIcon, SpinnerIcon } from '../../components/icons/icons';
import RepertoireList from '../../components/repertoire-list/repertoire-list.component';
import { MovementType } from '../../components/search/movement-button.component';
import page from './my-repertoire.module.css';
import { planMoves, reorder } from './repertoire-order';
import StageMode from '../../components/stage-mode/stage-mode.component';
import ConfirmationDialog from '../../components/dialogs/confirmation.dialog';

// Space kept free below the sheet so the page does not grow a scrollbar
const SHEET_BOTTOM_MARGIN = 16;
const SHEET_MIN_HEIGHT = 200;

type RepertoireView = 'sheets' | 'list';
const VIEW_STORAGE_KEY = 'pb-repertoire-view';

const readView = (): RepertoireView => {
    try {
        return localStorage.getItem(VIEW_STORAGE_KEY) === 'list' ? 'list' : 'sheets';
    } catch {
        return 'sheets';
    }
}

export default function MyRepertoire() {
    const [repertoire, setRepertoire] = useState<FileResponse[]>();
    const [searching, setSearching] = useState(false);
    const [downloadingRepertoire, setDownloadingRepertoire] = useState(false);
    const [sheetHeight, setSheetHeight] = useState<number>();
    const slideRef = useRef<HTMLDivElement>(null);
    const slideshowRef = useRef<SlideshowRef>(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [stageOpen, setStageOpen] = useState(false);
    const [view, setView] = useState<RepertoireView>(readView);
    const [moving, setMoving] = useState(false);
    const [confirmCleanVisible, setConfirmCleanVisible] = useState(false);

    const changeView = (next: RepertoireView) => {
        setView(next);
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, next);
        } catch { }
    }

    // Opening a sheet from the list shows it in the sheets view
    const openSheet = (index: number) => {
        setCurrentSlide(index);
        changeView('sheets');
    }

    // Return to the slide the musician stopped at in stage mode
    const onStageClose = useCallback((index: number) => {
        setStageOpen(false);
        setCurrentSlide(index);
        slideshowRef.current?.goTo(index);
    }, []);

    const service = useRepertoireService();

    // A silent reload keeps the current view on screen instead of swapping it for the loader
    const loadRepertoire = async (silent: boolean = false) => {
        try {
            if (!silent) setSearching(true);

            const { data } = await service.get();

            // An empty repertoire may come as an empty list, a null list or no body at all
            setRepertoire(data?.sheets?.length ? data.sheets : undefined);
        }
        catch {
            toast.warn('Não foi possível carregar o repertório!');
        }
        finally {
            setSearching(false);
        }
    }

    // Load once: the service is a new object on every render, so depending on it would refetch forever
    // while the repertoire is empty
    useEffect(() => { loadRepertoire(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useLayoutEffect(() => {
        const updateSheetHeight = () => {
            if (!slideRef.current) return;

            const top = slideRef.current.getBoundingClientRect().top + window.scrollY;
            const available = window.innerHeight - top - SHEET_BOTTOM_MARGIN;

            setSheetHeight(Math.max(available, SHEET_MIN_HEIGHT));
        };

        updateSheetHeight();
        window.addEventListener('resize', updateSheetHeight);

        return () => window.removeEventListener('resize', updateSheetHeight);
    }, [repertoire, searching, view])

    const actions: Record<MovementType, (f: FileResponse) => Promise<any>> = {
        [MovementType.Up]: f => service.up(f),
        [MovementType.First]: f => service.first(f),
        [MovementType.Down]: f => service.down(f),
        [MovementType.Last]: f => service.last(f),
        [MovementType.Remove]: f => service.remove(f),
    };

    const moveSheet = async (file: FileResponse, type: MovementType, notify: boolean) => {
        setMoving(true);
        setRepertoire(items => {
            if (!items) return items;

            const next = reorder(items, file, type);
            return next.length > 0 ? next : undefined;
        });

        try {
            await actions[type](file);

            if (type === MovementType.Remove)
                toast.success(`Partitura removida do repertório.`);
            else if (notify)
                toast.success(`Partitura movimentada com sucesso.`);
        } catch {
            toast.error(type === MovementType.Remove
                ? `Não foi possivel remover a partitura.`
                : `Não foi possivel movimentar a partitura.`);
        }
        finally {
            // Sync with the server either way, so a failed move puts the old order back
            await loadRepertoire(true);
            setMoving(false);
        }
    }

    // Drag and drop in the list: show the new order at once, then replay it on the API step by step
    const reorderSheet = async (file: FileResponse, from: number, to: number) => {
        const plan = planMoves(from, to, repertoire?.length ?? 0);
        if (plan.length === 0) return;

        setMoving(true);
        setRepertoire(items => items ? arrayMove(items, from, to) : items);

        try {
            for (const step of plan)
                await actions[step](file);
        } catch {
            toast.error(`Não foi possivel movimentar a partitura.`);
        }
        finally {
            await loadRepertoire(true);
            setMoving(false);
        }
    }

    // The sheets view gives no visual hint that the order changed, so it keeps the confirmation toast
    const onSlideMove = (type: MovementType) => async (file: FileResponse) => moveSheet(file, type, true);

    const onCleanButtonClick = async () => {
        try {
            await service.clear();
            await loadRepertoire();

            toast.success(`Repertório limpo com sucesso.`);
        } catch {
            toast.error(`Não foi possivel limpar o repertório.`)
        }
    }

    const onDownloadRepertoireClick = async () => {
        try {
            setDownloadingRepertoire(true);

            const { data } = await service.download();

            if (data) {
                const base64ByteArray = StringHelper.base64ToArrayBuffer(data);

                FileHelper.TryDownload(`Repertorio.pdf`, new Blob([base64ByteArray as any], { type: "application/pdf" }))
                toast.success(`Repertório baixado com sucesso.`);
            }
        } catch {
            toast.error(`Não foi possivel baixar o repertório.`)
        }
        finally {
            setDownloadingRepertoire(false);
        }
    }

    return <Page
        eyebrow='Repertório'
        title='Meu repertório'
        subtitle='Veja as partituras ou organize a ordem pela lista (arraste para mover), toque em tela cheia no modo palco ou baixe tudo em um único PDF.'>
        {repertoire && !searching && <div className={styles.toolbar}>
            <div className={page.viewSwitch} role='group' aria-label='Modo de visualização'>
                <button type='button' aria-pressed={view === 'sheets'} className={view === 'sheets' ? page.viewActive : undefined} onClick={() => changeView('sheets')}>
                    <GalleryIcon />
                    Partituras
                </button>
                <button type='button' aria-pressed={view === 'list'} className={view === 'list' ? page.viewActive : undefined} onClick={() => changeView('list')}>
                    <ListIcon />
                    Lista
                </button>
            </div>
            <button type='button' className={styles.button} onClick={() => setStageOpen(true)}>
                <MaximizeIcon />
                Modo palco
            </button>
            <button type='button' className={styles.buttonSecondary} onClick={() => setConfirmCleanVisible(true)}>
                <EraserIcon />
                Limpar repertório
            </button>
            <button type='button' className={styles.buttonSecondary} onClick={onDownloadRepertoireClick} disabled={downloadingRepertoire}>
                {downloadingRepertoire ? <SpinnerIcon /> : <DownloadIcon />}
                {downloadingRepertoire ? 'Gerando PDF...' : 'Baixar como PDF'}
            </button>
        </div>}

        {searching && <Loader label='Carregando seu repertório...' />}
        {repertoire && !searching && view === 'list' && <RepertoireList
            items={repertoire}
            busy={moving}
            onMove={(file, type) => moveSheet(file, type, false)}
            onReorder={reorderSheet}
            onOpen={openSheet} />}
        {repertoire && !searching && view === 'sheets' && <div ref={slideRef}><Slide
            ref={slideshowRef}
            autoplay={false}
            transitionDuration={500}
            defaultIndex={Math.min(currentSlide, repertoire.length - 1)}
            onChange={(_, to) => setCurrentSlide(to)}
        >
            {repertoire.map((value, index) => <SheetItem
                key={value.serverPath}
                item={value}
                upButton={{ visible: index !== 0, onClick: onSlideMove(MovementType.Up) }}
                firstButton={{ visible: index !== 0, onClick: onSlideMove(MovementType.First) }}
                downButton={{ visible: index !== repertoire.length - 1, onClick: onSlideMove(MovementType.Down) }}
                lastButton={{ visible: index !== repertoire.length - 1, onClick: onSlideMove(MovementType.Last) }}
                removeButton={{ visible: true, onClick: onSlideMove(MovementType.Remove) }}
                maxHeight={sheetHeight}
            />)} </Slide></div>}
        {stageOpen && repertoire && <StageMode items={repertoire} startIndex={currentSlide} onClose={onStageClose} />}
        {confirmCleanVisible && <ConfirmationDialog
            title='Limpar o repertório?'
            text={`Todas as ${repertoire?.length ?? 0} partitura(s) serão removidas do seu repertório. Essa ação não pode ser desfeita.`}
            onConfirm={onCleanButtonClick}
            onClose={() => setConfirmCleanVisible(false)}
        />}
        {!repertoire && !searching && <p className={styles.empty}>Seu repertório está vazio. Adicione partituras pela busca ou pelo acervo.</p>}

        {/* list with
    - image, pdf
    - button to remove

    * option to full screen
    * navigation at the page bottom
    (MUST) save locally the links
    (?) save images offline
    */}
    </Page>
}